/**
 * ChangeRequestService - Manage time tracking change requests via HttpService
 */

import httpService, { HttpResponse } from '../../../core/services/HttpService';

export interface ChangeRequest {
  id: number;
  time_tracking: number;
  original_start_time?: string | null;
  original_end_time?: string | null;
  original_work_area?: number | null;
  original_work_area__name?: string | null;
  // New employee review fields
  new_start_time?: string | null;
  new_end_time?: string | null;
  // Legacy/alternative fields kept for compatibility
  requested_start_time?: string | null;
  requested_end_time?: string | null;
  requested_work_area?: number | null;
  requested_work_area__name?: string | null;
  employee_notes?: string | null;
  proposed_break_duration?: number | null;
  original_break_duration?: number | null;
  status?: string | null;
  [key: string]: any;
}

export interface EmployeeReviewPayload {
  new_start_time?: string | null;
  new_end_time?: string | null;
  employee_notes?: string | null;
  proposed_break_duration?: number | null;
}

export interface ApproveOverridePayload {
  override_start_time?: string | null;
  override_end_time?: string | null;
  resolve_conflicts?: 'truncate';
}

class ChangeRequestService {
  static async create(payload: {
    time_tracking?: number;
    employee: number;
    new_start_time: string;
    new_end_time: string;
    break_minutes?: number;
    new_work_area?: number;
    reason?: string;
    employee_notes?: string;
    idempotency_key?: string;
  }, idempotencyKey?: string): Promise<ChangeRequest> {
    const res = await httpService.post<ChangeRequest>(
      `/api/time-tracking/change-requests/`,
      payload,
      idempotencyKey ? { headers: { 'X-Idempotency-Key': idempotencyKey } } : undefined
    );
    return res.data;
  }
  static async fetchByTimeTrackingId(timeTrackingId: number, status?: string): Promise<ChangeRequest | null> {
    const response: HttpResponse<ChangeRequest[]> = await httpService.get(`/api/time-tracking/change-requests/`, {
      params: {
        time_tracking: timeTrackingId,
        ...(status ? { status } : {})
      }
    });
    const list = response.data || [];
    return list.length > 0 ? list[0] : null;
  }

  static async fetchAllByStatus(status: string): Promise<ChangeRequest[]> {
    const response: HttpResponse<ChangeRequest[]> = await httpService.get(`/api/time-tracking/change-requests/`, {
      params: { status }
    });
    return response.data || [];
  }

  static async fetchById(id: number): Promise<ChangeRequest> {
    const response = await httpService.get<ChangeRequest>(`/api/time-tracking/change-requests/${id}/`);
    return response.data;
  }

  static async submitEmployeeReview(id: number, payload: EmployeeReviewPayload): Promise<ChangeRequest> {
    const response = await httpService.post<ChangeRequest>(`/api/time-tracking/change-requests/${id}/submit-employee-review/`, payload);
    return response.data;
  }

  static async approve(id: number, overrides?: ApproveOverridePayload): Promise<void> {
    if (overrides && (overrides.override_start_time || overrides.override_end_time || overrides.resolve_conflicts)) {
      await httpService.post(`/api/time-tracking/change-requests/${id}/approve/`, overrides);
      return;
    }
    await httpService.post(`/api/time-tracking/change-requests/${id}/approve/`);
  }

  static async reject(id: number): Promise<void> {
    await httpService.post(`/api/time-tracking/change-requests/${id}/reject/`);
  }

  static async reopen(id: number): Promise<void> {
    // Versuche verschiedene mögliche Endpunkte, um einen CR wieder zu öffnen
    // 1) offizieller Reopen-Endpoint
    try {
      await httpService.post(`/api/time-tracking/change-requests/${id}/reopen/`);
      return;
    } catch {}
    // 2) PATCH Status zurück auf PENDING
    try {
      await httpService.patch(`/api/time-tracking/change-requests/${id}/`, { status: 'PENDING' });
      return;
    } catch {}
    // 3) Fallback: Fehler werfen
    throw new Error('Reopen change request not supported by backend');
  }

  static async deleteChangeRequest(id: number): Promise<void> {
    await httpService.delete(`/api/time-tracking/change-requests/${id}/`);
  }
}

export default ChangeRequestService; 